import { Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, CheckCircle2, ExternalLink } from "lucide-react";

const BOOKING_URL =
  "https://app.squareup.com/appointments/book/cl8chxbw317ncd/L858HSHAJKEZC/start";

const perks = [
  {
    icon: Calendar,
    title: "Pick Your Date",
    text: "Browse real-time openings and reserve a slot that fits your schedule.",
  },
  {
    icon: Clock,
    title: "Flexible Sessions",
    text: "Private lessons, group training, and remote sessions available.",
  },
  {
    icon: CheckCircle2,
    title: "Instant Confirmation",
    text: "Secure your spot through Square and get an email confirmation immediately.",
  },
];

const BookingPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      {/* Top Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link
              to="/"
              className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight text-slate-900 hover:text-red-600 transition-colors"
              data-testid="booking-nav-logo"
            >
              CRIPPS BASEBALL
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors uppercase tracking-wider"
              data-testid="booking-back-home"
            >
              <ArrowLeft size={16} />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 md:px-12 lg:px-24 bg-slate-50 border-b border-slate-100">
        <div className="max-w-5xl mx-auto text-left">
          <p className="text-sm font-medium tracking-widest uppercase text-slate-500 mb-4">
            Schedule Your Training
          </p>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-black tracking-tighter uppercase text-slate-900 mb-6">
            BOOK A<br />SESSION
          </h1>
          <p className="text-slate-600 leading-relaxed max-w-2xl mb-8 text-lg">
            Reserve your lesson through our secure Square booking portal.
            Choose the service, pick a time, and confirm — it&apos;s that simple.
          </p>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center gap-3"
            data-testid="booking-cta-primary"
          >
            Book on Square
            <ExternalLink size={18} />
          </a>
        </div>
      </section>

      {/* Perks */}
      <section className="py-16 md:py-20 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8 md:gap-10">
          {perks.map((perk, index) => (
            <div
              key={perk.title}
              className="border-l-4 border-red-600 pl-6 py-2"
              data-testid={`booking-perk-${index + 1}`}
            >
              <perk.icon className="w-8 h-8 text-slate-900 mb-4" />
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold tracking-tight uppercase text-slate-900 mb-2">
                {perk.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{perk.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Booking Widget */}
      <section className="pb-20 md:pb-32 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <p className="text-sm font-medium tracking-widest uppercase text-slate-400 mb-3">
              Secure Booking
            </p>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold tracking-tight uppercase text-slate-900">
              RESERVE YOUR SPOT
            </h2>
          </div>

          <div className="bg-slate-900 text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold tracking-tight uppercase mb-2">
                Ready to train?
              </p>
              <p className="text-slate-300 max-w-md">
                Tap the button to open the Square booking page in a new tab. Your
                booking is processed securely by Square.
              </p>
            </div>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-red-600 hover:bg-red-700 text-white px-8 py-4 font-bold uppercase tracking-wider transition-colors whitespace-nowrap"
              data-testid="booking-cta-secondary"
            >
              Book Now
              <ExternalLink size={18} />
            </a>
          </div>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Prefer to talk first? Call or text{" "}
            <a
              href="tel:2086591837"
              className="text-slate-900 font-medium hover:text-red-600 transition-colors"
              data-testid="booking-phone-link"
            >
              (208) 659-1837
            </a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-12 lg:px-24 bg-slate-950">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © 2024 Cripps Baseball Training. All rights reserved.
          </p>
          <p className="font-['Barlow_Condensed'] text-slate-600 text-sm uppercase tracking-wider">
            Train Hard. Play Harder.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default BookingPage;
